import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import DateTimeFiltersViewModel from "./ViewModels/DateTimeFiltersViewModel";
import moment from "moment";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers';

interface IDateTimeComponentProps {
    dateTimeFilters: DateTimeFiltersViewModel,
    setDateTimeFilters: (value: DateTimeFiltersViewModel) => void,
    isAgent: boolean;
}

export default function DateTimeComponent({ dateTimeFilters, setDateTimeFilters, isAgent }: IDateTimeComponentProps) {
    const [minDateLimit, setMinDateLimit] = useState<any>(moment());
    const [maxDateLimit, setMaxDateLimit] = useState<any>(moment());
    const [startTime, setStartTime] = useState<any>(setTimeSlice(moment()));
    const [endTime, setEndTime] = useState<any>(setTimeSlice(moment()));

    const presets = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Single Date', 'Date Range']
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

    useEffect(() => {
        if (dateTimeFilters.dayOfWeek === '') {
            setDateTimeFilters({
                ...dateTimeFilters,
                dayOfWeek: daysOfWeek[6]
            })
        }
    }, [dateTimeFilters])

    useEffect(() => {
        if (dateTimeFilters.startTime) {
            let start = moment()
            let startNums = dateTimeFilters.startTime.split(':');
            const startHour = parseInt(startNums[0])
            const startMin = parseInt(startNums[1])
            start.hour(startHour);
            start.minute(startMin);
            setStartTime(start);
        }

        if (dateTimeFilters.endTime) {
            let end = moment()
            let endNums = dateTimeFilters.endTime.split(":");
            const endHour = parseInt(endNums[0])
            const endMin = parseInt(endNums[1])
            end.hour(endHour);
            end.minute(endMin);
            setEndTime(end);
        }

        let minDate = moment().subtract(1, "month");
        minDate = minDate.set("date", 1);
        setMinDateLimit(minDate);
        const maxDate = moment().subtract(1, "day");
        setMaxDateLimit(maxDate);

        setUndefinedTime();
    }, [])

    const setUndefinedTime = () => {
        let timefilters = { ...dateTimeFilters };
        let currentTime = setTimeSlice(moment());

        if (timefilters.startTime === undefined)
            timefilters.startTime = currentTime.format("HH:mm");

        if (timefilters.endTime === undefined)
            timefilters.endTime = currentTime.format("HH:mm");

        setDateTimeFilters(timefilters);
    }

    const getItems = (items: string[]) => {
        return items.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)
    }

    const handleFilterChange = (e: any) => {
        setDateTimeFilters({
            ...dateTimeFilters,
            [e.target.name]: e.target.value
        })
    }

    const handleCheckboxChange = (value: boolean) => {
        setDateTimeFilters({
            ...dateTimeFilters,
            flagTimeRange: value
        });
    }

    const handleDate = (filterName: string, value: moment.Moment | null) => {
        let dateValue = value != null ? value.toDate() : null;
        setDateTimeFilters({
            ...dateTimeFilters,
            [filterName]: dateValue
        })
    };

    const onTimeChanged = (name: string, value: moment.Moment) => {
        let newTime = value.clone();
        if (newTime !== null) {
            newTime = setTimeSlice(newTime);
        }
        let timeString: string = newTime.format("HH:mm");
        if (name === 'startTime') {
            setStartTime(newTime);
            setDateTimeFilters({
                ...dateTimeFilters,
                startTime: timeString
            });
        }
        else if (name === 'endTime') {
            setEndTime(newTime);
            setDateTimeFilters({
                ...dateTimeFilters,
                endTime: timeString
            });
        }
    }

    function setTimeSlice(newTime: moment.Moment) {
        if (newTime.minute() > 0 && newTime.minute() < 15) {
            newTime.minute(0);
        }
        if (newTime.minute() > 15 && newTime.minute() < 30) {
            newTime.minute(15);
        }
        if (newTime.minute() > 30 && newTime.minute() < 45) {
            newTime.minute(30);
        }
        if (newTime.minute() > 45) {
            newTime.minute(45);
        }
        return newTime;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container height="100%">
                <Grid size={12}>
                    <InputLabel>Date Preset</InputLabel>
                    <Select
                        name="preset"
                        onChange={handleFilterChange}
                        value={dateTimeFilters.preset}
                        fullWidth
                    >
                        {
                            getItems(presets)
                        }
                    </Select>
                </Grid>
                {(dateTimeFilters.preset === "This Week" || dateTimeFilters.preset === "Last Week") &&
                    <Grid size={12}>
                        <InputLabel>Week Starting On</InputLabel>
                        <Select
                            name="dayOfWeek"
                            onChange={handleFilterChange}
                            value={dateTimeFilters.dayOfWeek}
                            fullWidth
                        >
                            {
                                getItems(daysOfWeek)
                            }
                        </Select>
                    </Grid>
                }
                {dateTimeFilters.preset === "Single Date" &&
                    <Grid size={12}>
                        <InputLabel>Single Date</InputLabel>
                        {isAgent ?
                            <DatePicker
                                onChange={(newDate: moment.Moment | null) => handleDate("startDate", newDate)}
                                format={"DD/MM/YYYY"} value={moment(dateTimeFilters.startDate)}
                                maxDate={maxDateLimit}
                                minDate={minDateLimit}
                                slotProps={{
                                    textField: {
                                        inputProps: { readOnly: true },
                                    },
                                }} />
                            :
                            <DatePicker
                                onChange={(newDate: moment.Moment | null) => handleDate("startDate", newDate)}
                                format={"DD/MM/YYYY"}
                                value={moment(dateTimeFilters.startDate)}
                                maxDate={maxDateLimit}
                                slotProps={{
                                    textField: {
                                        inputProps: { readOnly: true },
                                    },
                                }} />
                        }
                    </Grid>
                }
                {dateTimeFilters.preset === "Date Range" &&
                    <>
                        <Grid size={6}>
                            <InputLabel>Start Date</InputLabel>
                            {isAgent ?
                                <DatePicker
                                    onChange={(newDate: moment.Moment | null) => handleDate("startDate", newDate)}
                                    format={"DD/MM/YYYY"} value={moment(dateTimeFilters.startDate)}
                                    maxDate={maxDateLimit}
                                    minDate={minDateLimit}
                                    slotProps={{
                                        textField: {
                                            inputProps: { readOnly: true },
                                        },
                                    }} />
                                :
                                <DatePicker
                                    onChange={(newDate: moment.Moment | null) => handleDate("startDate", newDate)}
                                    format={"DD/MM/YYYY"}
                                    value={moment(dateTimeFilters.startDate)}
                                    maxDate={maxDateLimit}
                                    slotProps={{
                                        textField: {
                                            inputProps: { readOnly: true },
                                        },
                                    }} />
                            }
                        </Grid>
                        <Grid size={6}>
                            <InputLabel>End Date</InputLabel>
                            {isAgent ?
                                <DatePicker
                                    onChange={(newDate: moment.Moment | null) => handleDate("endDate", newDate)}
                                    format={"DD/MM/YYYY"}
                                    value={moment(dateTimeFilters.endDate)}
                                    maxDate={maxDateLimit}
                                    minDate={minDateLimit}
                                    slotProps={{
                                        textField: {
                                            inputProps: { readOnly: true },
                                        },
                                    }} />
                                :
                                <DatePicker
                                    onChange={(newDate: moment.Moment | null) => handleDate("endDate", newDate)}
                                    maxDate={maxDateLimit}
                                    format={"DD/MM/YYYY"}
                                    value={moment(dateTimeFilters.endDate)}
                                    slotProps={{
                                        textField: {
                                            inputProps: { readOnly: true },
                                        },
                                    }} />
                            }
                        </Grid>
                    </>
                }
                <Grid size={12}>
                    <FormControlLabel control={<Checkbox checked={dateTimeFilters.flagTimeRange} onChange={() => handleCheckboxChange(!dateTimeFilters.flagTimeRange)} />} label="Time Range" />
                </Grid>
                {dateTimeFilters.flagTimeRange &&
                    <>
                        <Grid size={6}>
                            <InputLabel>Start Time</InputLabel>
                            <TimePicker
                                value={startTime}
                                onChange={(newDate: moment.Moment) => onTimeChanged('startTime', newDate)}
                                ampm={false}
                                minutesStep={15}
                                skipDisabled
                            />
                        </Grid>
                        <Grid size={6}>
                            <InputLabel>End Time</InputLabel>
                            <TimePicker
                                value={endTime}
                                onChange={(newDate: moment.Moment) => onTimeChanged('endTime', newDate)}
                                ampm={false}
                                minutesStep={15}
                                skipDisabled
                            />
                        </Grid>
                    </>
                }
            </Grid>
        </LocalizationProvider>
    );
}